import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Role definitions
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  CITY_ADMIN: 'city_admin',
  USER: 'user'
};

// City list for generating city admin credentials
const CITY_NAMES = ['Delhi', 'Mumbai', 'Palava', 'Chennai', 'Ahmedabad', 'Pune', 'Hyderabad', 'Kolkata', 'Bangalore'];

// Generate city admin credentials dynamically
const generateCityAdminCredentials = () => {
  const credentials = {};
  CITY_NAMES.forEach(city => {
    const cityLower = city.toLowerCase();
    credentials[`${cityLower}_admin`] = {
      password: `${cityLower}admin_123@`,
      role: ROLES.CITY_ADMIN,
      city: city
    };
  });
  return credentials;
};

// User credentials (in production, this should be managed by backend)
const USER_CREDENTIALS = {
  'super_admin': { password: 'superadmin123@#', role: ROLES.SUPER_ADMIN, city: null },
  'admin': { password: 'admin', role: ROLES.ADMIN, city: null },
  'user': { password: 'user', role: ROLES.USER, city: null },
  ...generateCityAdminCredentials()
};

// Restore session from localStorage synchronously so first render shows correct auth state (no flash)
const getInitialSession = () => {
  try {
    const sessionData = localStorage.getItem('authSession');
    if (sessionData) {
      const session = JSON.parse(sessionData);
      if (session.isAuthenticated && session.username) {
        return {
          isAuthenticated: true,
          currentUsername: session.username,
          userRole: session.role || ROLES.USER,
          userCity: session.city ?? null
        };
      }
    }
  } catch (e) {
    console.error('Error loading session:', e);
  }
  return {
    isAuthenticated: false,
    currentUsername: '',
    userRole: null,
    userCity: null
  };
};

const initialSession = getInitialSession();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(initialSession.isAuthenticated);
  const [currentUsername, setCurrentUsername] = useState(initialSession.currentUsername);
  const [userRole, setUserRole] = useState(initialSession.userRole);
  const [userCity, setUserCity] = useState(initialSession.userCity); // City assigned to city admin
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [pendingCitySelection, setPendingCitySelection] = useState(null);

  const saveSession = useCallback((username, role, city = null) => {
    localStorage.setItem('authSession', JSON.stringify({
      isAuthenticated: true,
      username: username,
      role: role,
      city: city,
      timestamp: Date.now()
    }));
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('authSession');
    setIsAuthenticated(false);
    setCurrentUsername('');
    setUserRole(null);
    setUserCity(null);
  }, []);

  const loadSession = useCallback(() => {
    const sessionData = localStorage.getItem('authSession');
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData);
        if (session.isAuthenticated && session.username) {
          setIsAuthenticated(true);
          setCurrentUsername(session.username);
          setUserRole(session.role || ROLES.USER);
          setUserCity(session.city || null);
          return true;
        }
      } catch (e) {
        console.error('Error loading session:', e);
        clearSession();
      }
    }
    return false;
  }, [clearSession]);

  // Restore session on mount (e.g. after refresh) so user stays logged in until they logout
  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const login = useCallback((username, password) => {
    // Check credentials
    const user = USER_CREDENTIALS[username.toLowerCase()];
    
    if (user && user.password === password) {
      setIsAuthenticated(true);
      setCurrentUsername(username);
      setUserRole(user.role);
      setUserCity(user.city || null);
      saveSession(username, user.role, user.city || null);
      return { success: true, role: user.role, city: user.city || null };
    }
    
    // Backward compatibility: admin/admin
    if (username === 'admin' && password === 'admin') {
      setIsAuthenticated(true);
      setCurrentUsername(username);
      setUserRole(ROLES.ADMIN);
      setUserCity(null);
      saveSession(username, ROLES.ADMIN, null);
      return { success: true, role: ROLES.ADMIN, city: null };
    }
    
    return { success: false, error: 'Invalid username or password. Please try again.' };
  }, [saveSession]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const openAuthModal = useCallback((tab = 'login', pendingCity = null) => {
    setAuthTab('login');
    setPendingCitySelection(pendingCity);
    setShowAuthModal(true);
  }, []);

  const closeAuthModal = useCallback((clearPending = false) => {
    setShowAuthModal(false);
    if (clearPending) {
      setPendingCitySelection(null);
    }
  }, []);

  // Check if user has admin privileges
  const isAdmin = useCallback(() => {
    return userRole === ROLES.ADMIN || userRole === ROLES.SUPER_ADMIN || userRole === ROLES.CITY_ADMIN;
  }, [userRole]);

  // Check if user is super admin
  const isSuperAdmin = useCallback(() => {
    return userRole === ROLES.SUPER_ADMIN;
  }, [userRole]);

  // Check if user is city admin
  const isCityAdmin = useCallback(() => {
    return userRole === ROLES.CITY_ADMIN;
  }, [userRole]);

  // Check if user can manage a specific city
  const canManageCity = useCallback((city) => {
    if (userRole === ROLES.SUPER_ADMIN) return true;
    if (userRole === ROLES.CITY_ADMIN && userCity === city) return true;
    return false;
  }, [userRole, userCity]);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    return userRole === role;
  }, [userRole]);

  const value = {
    isAuthenticated,
    currentUsername,
    userRole,
    userCity,
    showAuthModal,
    authTab,
    pendingCitySelection,
    login,
    logout,
    openAuthModal,
    closeAuthModal,
    setAuthTab,
    setPendingCitySelection,
    isAdmin,
    isSuperAdmin,
    isCityAdmin,
    canManageCity,
    hasRole,
    ROLES
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

